import {
    mdiChevronDown,
    mdiChevronRight,
    mdiChevronUp,
    mdiClipboardTextOutline,
    mdiLinkVariantPlus,
    mdiLinkVariantRemove,
    mdiPencil,
} from '@mdi/js';
import Icon from '@mdi/react';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CoreFetch, MessageLinkEntityType } from '../../../api';
import { ClaimApi, ProviderClaim, SupportClaim } from '../../../api/server';
import { MessagingContext } from '../../../services';
import { formatDate } from '../../../services/primitives/date-utils';
import { Tooltip, Variables } from '../../base';
import { createClaimConfig } from '../entity-manager/entity-configs';
import {
    rememberMessagingReturnForClaimEditor,
    withClaimEditorFromMessagingQuery,
} from '../claim-editor-return';
import { UniversalEntitySearchInput } from '../universal-claims-search-input';
import * as S from './claim-card.styles';

export interface IClaimCardProps {
    claimIds: string[];
    messageId?: string;
    onClaimAdd?: (claimId: string) => Promise<void>;
    onBulkClaimsAdd?: (claimIds: string[]) => Promise<void>;
    onClaimRemove?: (claimId: string) => Promise<void>;
}

const formatAmount = (amount: ProviderClaim['chargeAmount'] | SupportClaim['chargeAmount']): string => {
    if (!amount || !amount.amount) {
        return '$0.00';
    }
    const value = parseFloat(amount.amount);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: amount.currency || 'USD',
    }).format(value);
};

const getPatientName = (claim: SupportClaim | ProviderClaim): string => {
    const firstName = claim.patientFirst || '';
    const lastName = claim.patientLast || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
};

const getAccNumber = (claim: SupportClaim | ProviderClaim): string => {
    return claim.accountNumber || claim.id || '—';
};

export const ClaimCard: React.FunctionComponent<IClaimCardProps> = ({
    claimIds,
    messageId,
    onClaimAdd,
    onBulkClaimsAdd,
    onClaimRemove,
}) => {
    const context = useContext(MessagingContext);
    const { accountId } = useParams<{ accountId?: string }>();
    const isSupportApp = context.account?.supportGrant === true;
    const canEditClaims =
        !!(onClaimAdd && onClaimRemove) ||
        (!!messageId && !!context.onLinkEntityToMessage && !!context.onUnlinkEntityFromMessage);

    const [claims, setClaims] = useState<(SupportClaim | ProviderClaim)[]>([]);
    const [loading, setLoading] = useState(true);
    const [linkingClaimIds, setLinkingClaimIds] = useState<string[]>([]);
    const [expanded, setExpanded] = useState(true);

    const claimConfig = useMemo(
        () =>
            createClaimConfig(
                isSupportApp,
                accountId || context.account?.grantedAccountId,
                context.onClaimSearch,
            ),
        [isSupportApp, accountId, context.account?.grantedAccountId, context.onClaimSearch],
    );

    const claimIdsKey = useMemo(
        () => (claimIds?.length ? [...claimIds].sort().join(',') : ''),
        [claimIds],
    );

    useEffect(() => {
        const fetchClaims = async () => {
            if (!claimIds || claimIds.length === 0) {
                setClaims([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                // Ask the host app first when it offers a resolver — it may be able to answer
                // without a request at all, which is the only way this card can show a linked claim
                // in demo mode (NS-14: the boundary refuses the fetch below, by design).
                if (context.onClaimsResolve) {
                    const resolved = await context.onClaimsResolve(claimIds);
                    setClaims(resolved || []);
                    return;
                }
                const filterIds = claimIds.map(id => `EQ:${id}`);
                const fetchedClaims = isSupportApp
                    ? await CoreFetch(ClaimApi).listSupportClaims({ filterId: filterIds })
                    : await CoreFetch(ClaimApi).listProviderClaims({ filterId: filterIds });
                setClaims(fetchedClaims || []);
            } catch (error) {
                console.error('Failed to fetch claims:', error);
                setClaims([]);
            } finally {
                setLoading(false);
            }
        };

        fetchClaims();
    }, [claimIdsKey, isSupportApp, messageId, context.onClaimsResolve]);

    const openClaim = (claimId: string) => {
        const accId = accountId || context.account?.grantedAccountId;
        if (!accId || !claimId) {
            return;
        }
        window.open(`/${accId}/claims/${claimId}`, '_blank', 'noopener,noreferrer');
    };

    const openClaimEditor = (claimId: string) => {
        const accId = accountId || context.account?.grantedAccountId;
        if (!accId || !claimId) {
            return;
        }
        rememberMessagingReturnForClaimEditor(messageId || context.message?.id);
        const editUrl = withClaimEditorFromMessagingQuery(`/${accId}/claims/${claimId}/edit`);
        window.open(editUrl, '_blank', 'noopener,noreferrer');
    };

    const linkClaim = async (claimId: string): Promise<void> => {
        if (!claimId || claimIds.includes(claimId) || linkingClaimIds.includes(claimId)) {
            return;
        }

        setLinkingClaimIds(current => [...current, claimId]);
        try {
            if (onClaimAdd) {
                await onClaimAdd(claimId);
                return;
            }
            if (!messageId || !context.onLinkEntityToMessage) {
                return;
            }
            await context.onLinkEntityToMessage(messageId, MessageLinkEntityType.CLAIM, [claimId]);
            context.onMessageFieldsUpdate?.();
        } catch (error) {
            console.error('Failed to link claim to message:', error);
        } finally {
            setLinkingClaimIds(current => current.filter(id => id !== claimId));
        }
    };

    const linkClaims = async (selectedClaimIds: string[]): Promise<void> => {
        const uniqueIds = _.uniq(selectedClaimIds).filter(
            id => id && !claimIds.includes(id) && !linkingClaimIds.includes(id),
        );
        if (!uniqueIds.length) {
            return;
        }
        if (onBulkClaimsAdd) {
            setLinkingClaimIds(current => [...current, ...uniqueIds]);
            try {
                await onBulkClaimsAdd(uniqueIds);
            } catch (error) {
                console.error('Failed to link claims:', error);
            } finally {
                setLinkingClaimIds(current => current.filter(id => !uniqueIds.includes(id)));
            }
            return;
        }
        for (const claimId of uniqueIds) {
            await linkClaim(claimId);
        }
    };

    const unlinkClaim = async (claimId: string): Promise<void> => {
        if (!claimId || linkingClaimIds.includes(claimId)) {
            return;
        }

        setLinkingClaimIds(current => [...current, claimId]);
        try {
            if (onClaimRemove) {
                await onClaimRemove(claimId);
                return;
            }
            if (!messageId || !context.onUnlinkEntityFromMessage) {
                return;
            }
            await context.onUnlinkEntityFromMessage(messageId, MessageLinkEntityType.CLAIM, [claimId]);
            context.onMessageFieldsUpdate?.();
        } catch (error) {
            console.error('Failed to unlink claim from message:', error);
        } finally {
            setLinkingClaimIds(current => current.filter(id => id !== claimId));
        }
    };

    const total = useMemo(() => {
        const sum = claims.reduce((acc, claim) => {
            const value = claim.chargeAmount?.amount ? parseFloat(claim.chargeAmount.amount) : 0;
            return acc + value;
        }, 0);
        const currency = claims[0]?.chargeAmount?.currency || 'USD';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
        }).format(sum);
    }, [claims]);

    if (loading) {
        return (
            <S.Section>
                <S.SectionLabel>Claim</S.SectionLabel>
                <S.LoadingState>Loading claims...</S.LoadingState>
            </S.Section>
        );
    }

    if (!claims.length && !canEditClaims) {
        return null;
    }

    const multi = claims.length > 1;
    const primary = claims[0];

    return (
        <S.Section>
            <S.SectionLabel>{multi || !claims.length ? 'Claims' : 'Claim'}</S.SectionLabel>
            {/* Search sits outside Card so its dropdown is not clipped by the Card overflow. */}
            {canEditClaims && (
                <S.ClaimSearch>
                    <Tooltip
                        direction="top"
                        title={<span>{claims.length ? 'Link another claim' : 'Link a claim'}</span>}
                    >
                        <S.ClaimSearchLabel>
                            <Icon path={mdiLinkVariantPlus} size={0.65} />
                            {claims.length ? 'Link another claim' : 'Link a claim'}
                        </S.ClaimSearchLabel>
                    </Tooltip>
                    <UniversalEntitySearchInput<SupportClaim | ProviderClaim>
                        entityConfig={claimConfig}
                        onEntitySelected={linkClaim}
                        onBulkEntitiesSelected={linkClaims}
                    />
                </S.ClaimSearch>
            )}
            {!claims.length ? (
                <S.EmptyState>No claims linked</S.EmptyState>
            ) : (
                <S.Card>
                <S.CardHeader $flush={multi && !expanded}>
                    <Icon path={mdiClipboardTextOutline} size={0.75} color={Variables.ThemeLight.TextMedium} />
                    <S.PatientName>{getPatientName(primary)}</S.PatientName>
                    {primary.patientDob && (
                        <S.PatientMeta className="tabular">
                            DOB {formatDate(primary.patientDob, 'yyyy/MM/dd')}
                        </S.PatientMeta>
                    )}
                    {multi ? (
                        <S.MultiBadge className="tabular">{claims.length} claims</S.MultiBadge>
                    ) : (
                        primary.id && (
                            <S.OpenLink type="button" variant="unstyled" onClick={() => openClaim(primary.id!)}>
                                Open claim
                                <Icon path={mdiChevronRight} size={0.65} />
                            </S.OpenLink>
                        )
                    )}
                    {multi ? (
                        <Tooltip
                            direction="top"
                            title={<span>{expanded ? 'Collapse claims' : 'Expand claims'}</span>}
                        >
                            <S.ExpandButton
                                type="button"
                                variant="unstyled"
                                aria-expanded={expanded}
                                aria-label={expanded ? 'Collapse claims' : 'Expand claims'}
                                onClick={() => setExpanded(current => !current)}
                            >
                                <Icon path={expanded ? mdiChevronUp : mdiChevronDown} size={0.75} />
                            </S.ExpandButton>
                        </Tooltip>
                    ) : (
                        canEditClaims &&
                        primary.id && (
                            <Tooltip direction="top" title={<span>Unlink claim</span>}>
                                <S.UnlinkButton
                                    type="button"
                                    variant="unstyled"
                                    aria-label="Unlink claim"
                                    onClick={() => unlinkClaim(primary.id!)}
                                    disabled={linkingClaimIds.includes(primary.id)}>
                                    <Icon path={mdiLinkVariantRemove} size={0.68} />
                                </S.UnlinkButton>
                            </Tooltip>
                        )
                    )}
                </S.CardHeader>

                {multi ? (
                    expanded ? (
                    <>
                        {claims.map(claim => (
                            <S.ClaimRow key={claim.id}>
                                <S.ClaimRowField $flex="0 0 78px">
                                    <S.MetricLabel>DOS</S.MetricLabel>
                                    <S.MetricValue className="tabular">
                                        {claim.fromDos ? formatDate(claim.fromDos, 'MMM dd yyyy') : '—'}
                                    </S.MetricValue>
                                </S.ClaimRowField>
                                <S.ClaimRowField>
                                    <S.MetricLabel>Account Number</S.MetricLabel>
                                    <S.ClaimIdValue
                                        className="tabular"
                                        title={getAccNumber(claim)}>
                                        {getAccNumber(claim)}
                                    </S.ClaimIdValue>
                                </S.ClaimRowField>
                                <S.ClaimRowField $flex="0 0 auto">
                                    <S.MetricLabel>Charge</S.MetricLabel>
                                    <S.MetricValue className="tabular">
                                        {formatAmount(claim.chargeAmount)}
                                    </S.MetricValue>
                                </S.ClaimRowField>
                                <S.RowActions>
                                    {claim.id && (
                                        <Tooltip direction="top" title={<span>Open claim</span>}>
                                            <S.RowOpenButton
                                                type="button"
                                                variant="unstyled"
                                                aria-label="Open claim"
                                                onClick={() => openClaim(claim.id!)}>
                                                <Icon path={mdiChevronRight} size={0.7} />
                                            </S.RowOpenButton>
                                        </Tooltip>
                                    )}
                                    {claim.id && (
                                        <Tooltip direction="top" title={<span>Edit claim in ENTER</span>}>
                                            <S.RowEditButton
                                                type="button"
                                                variant="unstyled"
                                                aria-label="Edit claim in ENTER"
                                                onClick={() => openClaimEditor(claim.id!)}>
                                                <Icon path={mdiPencil} size={0.65} />
                                            </S.RowEditButton>
                                        </Tooltip>
                                    )}
                                    {canEditClaims && claim.id && (
                                        <Tooltip direction="top" title={<span>Unlink claim</span>}>
                                            <S.RowUnlinkButton
                                                type="button"
                                                variant="unstyled"
                                                aria-label="Unlink claim"
                                                onClick={() => unlinkClaim(claim.id!)}
                                                disabled={linkingClaimIds.includes(claim.id)}>
                                                <Icon path={mdiLinkVariantRemove} size={0.68} />
                                            </S.RowUnlinkButton>
                                        </Tooltip>
                                    )}
                                </S.RowActions>
                            </S.ClaimRow>
                        ))}
                        <S.Footer>
                            <S.FooterMeta>
                                {claims.length} claims
                                {_.uniq(_.compact(_.map(claims, 'primaryPayerName'))).length > 0
                                    ? ` · ${_.uniq(_.compact(_.map(claims, 'primaryPayerName'))).join(', ')}`
                                    : ''}
                            </S.FooterMeta>
                            <S.FooterTotal className="tabular">{total}</S.FooterTotal>
                        </S.Footer>
                    </>
                    ) : null
                ) : (
                    <S.MetricsRow>
                        {[
                            ['DOS', primary.fromDos ? formatDate(primary.fromDos, 'MMM dd yyyy') : '—'],
                            ['Payer', primary.primaryPayerName || '—'],
                            ['Charge', formatAmount(primary.chargeAmount)],
                        ].map(([label, value], index) => (
                            <S.MetricCell key={label} $borderLeft={index > 0}>
                                <S.MetricLabel>{label}</S.MetricLabel>
                                <S.MetricValue className="tabular" title={value}>
                                    {value}
                                </S.MetricValue>
                            </S.MetricCell>
                        ))}
                    </S.MetricsRow>
                )}
                </S.Card>
            )}
        </S.Section>
    );
};
